import { GridColumns } from '@mui/x-data-grid';
import {
    ERPApiReadConfigurationApi,
    GetERPApiReadConfigurationsParams,
} from '../../../services/backendServices/Apis/ERPApiReadConfigurationApi';
import {
    createAutocompleteField,
    createTextField,
    createDropdownField,
    ISearchConfiguration,
    ISearchRequest,
    ISearchResponse,
    createUseSearchService,
} from '../../../reusableFeatures';
import { paginate } from '../../../utils';
import { BuyerCompanyOption } from '../../../hooks';
import { ERPApiReadConfigurationViewModel } from '../../../services/backendServices/ViewModels';
import { subDays } from 'date-fns';
import {
    createERPApiReadFilter,
    ERPApiReadFilterParams,
} from '../../../services/backendServices/Apis/ERPApiReadConfigurationApi/ERPApiReadConfigurationsForCSP.filter';

export interface ERPApiReadConfigurationSearchParams {
    buyerCompanyId: string | null;
    apiName: string | null;
    dateRange: string | null;
}

// Date range options for filtering
const DATE_RANGES = {
    ALL_TIME: 'any',
    PAST_DAY: '1',
    PAST_30_DAYS: '30',
    PAST_90_DAYS: '90',
    PAST_YEAR: '365',
};

const DATE_RANGE_OPTIONS = [
    { value: DATE_RANGES.ALL_TIME, label: 'Any (including no API reads)' },
    { value: DATE_RANGES.PAST_DAY, label: 'Past 24 Hours' },
    { value: DATE_RANGES.PAST_30_DAYS, label: 'Past 30 Days' },
    { value: DATE_RANGES.PAST_90_DAYS, label: 'Past 90 Days' },
    { value: DATE_RANGES.PAST_YEAR, label: 'Past Year' },
];

export class ERPApiReadConfigurationSearchService {
    private api = new ERPApiReadConfigurationApi();

    private buyerCompanyOptions: BuyerCompanyOption[];

    constructor(buyerCompanyOptions: BuyerCompanyOption[]) {
        this.buyerCompanyOptions = buyerCompanyOptions;
    }

    async fetchSearchConfiguration(): Promise<ISearchConfiguration<ERPApiReadConfigurationSearchParams>> {
        const searchFormFieldColumns = [
            [
                createAutocompleteField({
                    label: 'Buyer Company',
                    defaultValue: null,
                    dataBindingKey: 'buyerCompanyId',
                    options: this.buyerCompanyOptions,
                }),
                createTextField({
                    label: 'API Name',
                    defaultValue: '',
                    dataBindingKey: 'apiName',
                }),
                createDropdownField({
                    label: 'API Read Date Range',
                    defaultValue: DATE_RANGES.ALL_TIME,
                    dataBindingKey: 'dateRange',
                    options: DATE_RANGE_OPTIONS,
                }),
            ],
        ];

        const resultGridColumns: GridColumns = [
            {
                field: 'id',
                headerName: 'ID',
                width: 100,
                sortable: false,
            },
            {
                field: 'companyName',
                headerName: 'Buyer Company',
                width: 200,
                sortable: false,
            },
            {
                field: 'apiName',
                headerName: 'API Name',
                width: 200,
                sortable: false,
            },
            {
                field: 'lastApiRead',
                headerName: 'Last API Read',
                width: 180,
                sortable: false,
            },
        ];

        return {
            gridColumns: resultGridColumns,
            searchFormFieldColumns,
            enabledExportingMethods: [],
            pageSizeOptions: [5, 10, 25],
            defaultPageSize: 10,
            searchWithNoCriteria: true,
        };
    }

    async fetchResults(
        _searchConfiguration: any,
        searchRequest: ISearchRequest<ERPApiReadConfigurationSearchParams>
    ): Promise<ISearchResponse> {
        const { buyerCompanyId, apiName, dateRange } = searchRequest.searchQuery.state;

        // Build filter params for the helper function
        const filterParams: ERPApiReadFilterParams = {};

        if (buyerCompanyId) {
            filterParams.companyId = parseInt(buyerCompanyId, 10);
        }

        if (dateRange && dateRange !== DATE_RANGES.ALL_TIME) {
            const daysToSubtract = parseInt(dateRange, 10);
            const pastDate = subDays(new Date(), daysToSubtract);
            filterParams.lastApiReadDate = pastDate;
            filterParams.onlyIncludeApiReads = true;
        } else {
            // default behavior is to show all results regardless of API read; only omit them if specific date range selected
            filterParams.onlyIncludeApiReads = false;
        }

        // Create the filter string using the helper
        const filterExpression = createERPApiReadFilter(filterParams);

        // Build complete OData query
        const params: GetERPApiReadConfigurationsParams = {
            $select: 'Id,CompanyId,LastReadTime,ERPName',
            $orderby: 'Id asc',
            $count: 'true',
        };

        if (filterExpression) {
            params.$filter = filterExpression;
        }

        const result = await this.api.getERPApiReadConfigurations(params);
        let filteredData = result.data.Items as ERPApiReadConfigurationViewModel[];

        // Client-side filters
        if (apiName && apiName.length > 0) {
            filteredData = filteredData.filter((config) =>
                config.ERPName.toLowerCase().includes(apiName.toLowerCase())
            );
        }

        const items = filteredData.map((config: ERPApiReadConfigurationViewModel) => {
            const companyName =
                this.buyerCompanyOptions.find((option) => option.value === config.CompanyID?.toString())?.label ?? '';

            let apiReadDisplay = 'Never';

            if (config.LastReadTime) {
                const lastReadDate = new Date(config.LastReadTime);

                apiReadDisplay = lastReadDate.toLocaleString();
            }

            return {
                id: config.Id!,
                companyName,
                apiName: config.ERPName,
                lastApiRead: apiReadDisplay,
            };
        });

        return {
            pageResults: paginate(items, searchRequest.pageSize, searchRequest.pageNumber),
            totalResultCount: items.length,
        };
    }

    exportData(): Promise<void> {
        throw new Error('Method not implemented.');
    }

    get key(): string {
        return 'ERPApiReadConfiguration';
    }
}

export const useERPApiReadConfigurationSearchService = createUseSearchService(ERPApiReadConfigurationSearchService);
